import React, { useEffect, useState } from "react";
import { getBudgetStatus } from "../api";
import { formatCurrency } from "../utils/formatCurrency";
import ExpensesChart from "./ExpensesChart";
import ExpenseSubcategoryChart from "./ExpenseSubcategoryChart";

function LoanBar({ totalRepaid, loanTotal, loanMonthExpense, budget, loanEndDate }) {
  const pct = loanTotal > 0 ? Math.round((totalRepaid / loanTotal) * 100) : 0;
  const remaining = Math.max(0, loanTotal - totalRepaid);
  const monthPaid = loanMonthExpense ?? 0;
  const thisMonthPaid = budget > 0 && monthPaid >= budget * 0.9;

  return (
    <td style={{ minWidth: 220 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
        <span style={{ fontWeight: 500, color: thisMonthPaid ? "var(--success)" : monthPaid > 0 ? "var(--warning)" : "var(--text-muted)" }}>
          {thisMonthPaid ? "✓" : "○"} {formatCurrency(monthPaid)} ce mois
        </span>
        <span style={{ fontWeight: 700, fontSize: 12, color: pct >= 100 ? "var(--success)" : "var(--primary)" }}>{pct} %</span>
      </div>
      <div style={{ background: "#e8e8e8", borderRadius: 4, height: 6, overflow: "hidden", marginBottom: 4 }}>
        <div style={{ width: `${Math.min(pct, 100)}%`, background: pct >= 100 ? "var(--success)" : "var(--primary)", height: "100%", borderRadius: 4, transition: "width 0.3s" }} />
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
        {formatCurrency(totalRepaid)} remboursé / {formatCurrency(loanTotal)} — reste {formatCurrency(remaining)}
        {loanEndDate && <span> — fin {new Date(loanEndDate).toLocaleDateString("fr-FR")}</span>}
      </div>
    </td>
  );
}

function ProvisionBar({ totalSpentProvision, cumulativeBalance, annualTarget }) {
  const positive = cumulativeBalance >= 0;
  const balanceColor = positive ? "var(--success)" : "var(--danger)";
  const hasAnnual = annualTarget > 0;
  const barPct = hasAnnual ? Math.max(0, Math.min((cumulativeBalance / annualTarget) * 100, 100)) : 0;
  const barColor = !hasAnnual ? "var(--primary)" : cumulativeBalance < 0 ? "var(--danger)" : barPct >= 100 ? "var(--success)" : "var(--primary)";

  return (
    <td style={{ minWidth: 200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
        <span style={{ color: "var(--text-soft)" }}>{formatCurrency(totalSpentProvision)} dépensé</span>
        <span style={{ fontWeight: 700, color: balanceColor }}>
          {positive ? "+" : ""}{formatCurrency(cumulativeBalance)}
          {hasAnnual && <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 11 }}> / {formatCurrency(annualTarget)}</span>}
        </span>
      </div>
      {hasAnnual && (
        <div style={{ background: "var(--border)", borderRadius: 4, height: 6, overflow: "hidden" }}>
          <div style={{ width: `${barPct}%`, background: barColor, height: "100%", borderRadius: 4, transition: "width 0.3s" }} />
        </div>
      )}
    </td>
  );
}

function SpendingBar({ spent, budget, isSaving = false, targetDate }) {
  const pct = budget > 0 ? Math.round((spent / budget) * 100) : null;
  const barPct = pct !== null ? Math.min(pct, 100) : 0;

  const color = isSaving
    ? (pct >= 100 ? "var(--primary)" : "var(--success)")
    : pct === null   ? "#bbb"
    : pct >= 100     ? "var(--danger)"
    : pct >= 80      ? "var(--warning)"
    : "var(--success)";

  return (
    <td style={{ minWidth: 160 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
        <span style={{ fontWeight: 500, color: pct !== null && !isSaving && pct >= 100 ? "var(--danger)" : "var(--text)" }}>
          {formatCurrency(spent)}
        </span>
        {pct !== null && (
          <span style={{ color, fontWeight: 600, fontSize: 12 }}>{pct} %</span>
        )}
      </div>

      {budget > 0 && (
        <div style={{ background: "#e8e8e8", borderRadius: 4, height: 6, overflow: "hidden" }}>
          <div style={{ width: `${barPct}%`, background: color, height: "100%", borderRadius: 4, transition: "width 0.3s" }} />
        </div>
      )}

      {isSaving && targetDate && (
        <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 3 }}>
          avant {targetDate.slice(0, 10)}
        </div>
      )}
    </td>
  );
}

export default function Budget({ month }) {

  const [status, setStatus] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [collapsedCats, setCollapsedCats] = useState(new Set());

  const toggleCat = (cat) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  useEffect(() => {
    getBudgetStatus(month).then(stat => setStatus(stat.data)).catch(() => {});
  }, [month]);

  if (!status) {
    return <div style={{ color: "var(--text-soft)", padding: 24 }}>Chargement...</div>;
  }

  const remaining = Number(status.remaining || 0);

  const subcategories = status.subcategories || [];
  const allCats = [...new Set(subcategories.map(l => l.category_name))];
  const allCollapsed = allCats.length > 0 && allCats.every(c => collapsedCats.has(c));

  const grouped = allCats.map(cat => ({
    cat,
    items: subcategories.filter(l => l.category_name === cat),
  }));

  return (
    <div>

      {/* KPI RÉSUMÉ */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <div className="kpi-card">
          <div className="kpi-label">Budget total</div>
          <div className="kpi-value">{formatCurrency(Number(status.total_budget || 0))}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Dépensé</div>
          <div className="kpi-value kpi-danger">{formatCurrency(Number(status.total_spent || 0))}</div>
        </div>
        <div className="kpi-card" style={{ borderLeft: `4px solid ${remaining >= 0 ? "var(--success)" : "var(--danger)"}` }}>
          <div className="kpi-label">Reste</div>
          <div className={`kpi-value ${remaining >= 0 ? "kpi-success" : "kpi-danger"}`}>
            {formatCurrency(remaining)}
          </div>
        </div>
      </div>

      {/* GRAPHIQUES */}
      <div className="charts-row">
        <div>
          <ExpensesChart
            month={month}
            onSelectCategory={cat => setSelectedCategory(cat)}
          />
        </div>
        <div>
          {selectedCategory
            ? <ExpenseSubcategoryChart data={status?.subcategories || []} category={selectedCategory} month={month} />
            : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320, color: 'var(--text-muted)', fontSize: 14 }}>
                ← Cliquer une catégorie pour voir le détail
              </div>
          }
        </div>
      </div>

      {/* TABLEAU DÉTAIL */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Détail par sous-catégorie</h3>
        <button
          onClick={() => setCollapsedCats(allCollapsed ? new Set() : new Set(allCats))}
          style={{ fontSize: 12, padding: '4px 10px', cursor: 'pointer', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface)', color: 'var(--text-soft)' }}
        >
          {allCollapsed ? 'Tout développer' : 'Tout réduire'}
        </button>
      </div>

      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Catégorie</th>
              <th>Sous-catégorie</th>
              <th style={{ textAlign: "right" }}>Budget</th>
              <th>Dépensé / Progression</th>
            </tr>
          </thead>

          <tbody>
            {grouped.map(({ cat, items }) => {
              const isCollapsed = collapsedCats.has(cat);
              const totalBudget = items.reduce((s, l) => s + Number(l.budget || 0), 0);
              const totalSpent  = items.reduce((s, l) => s + Number(l.spent || 0), 0);
              const pct = totalBudget > 0 ? Math.round((totalSpent / totalBudget) * 100) : null;
              const pctColor = pct === null ? '#bbb' : pct >= 100 ? 'var(--danger)' : pct >= 80 ? 'var(--warning)' : 'var(--success)';

              return (
                <React.Fragment key={cat}>
                  {/* Ligne catégorie cliquable */}
                  <tr
                    onClick={() => toggleCat(cat)}
                    style={{ cursor: 'pointer', background: 'var(--surface-alt, #f5f5f5)', userSelect: 'none' }}
                  >
                    <td colSpan={2} style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', paddingTop: 10, paddingBottom: 10 }}>
                      <span style={{ marginRight: 8, fontSize: 11, color: 'var(--text-muted)' }}>{isCollapsed ? '▸' : '▾'}</span>
                      {cat}
                      {isCollapsed && (
                        <span style={{ marginLeft: 10, fontSize: 12, fontWeight: 400, color: 'var(--text-muted)' }}>
                          {items.length} sous-catégorie{items.length > 1 ? 's' : ''}
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: 600, fontSize: 13 }}>
                      {formatCurrency(totalBudget)}
                    </td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: isCollapsed ? 4 : 0 }}>
                        <span style={{ fontWeight: 500 }}>{formatCurrency(totalSpent)}</span>
                        {pct !== null && <span style={{ fontWeight: 600, fontSize: 12, color: pctColor }}>{pct} %</span>}
                      </div>
                      {isCollapsed && totalBudget > 0 && (
                        <div style={{ background: '#e8e8e8', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                          <div style={{ width: `${Math.min(pct ?? 0, 100)}%`, background: pctColor, height: '100%', borderRadius: 4 }} />
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Lignes sous-catégories */}
                  {!isCollapsed && items.map(line => (
                    <tr key={line.subcategory_id}>
                      <td style={{ color: "var(--text-soft)" }}>{line.category_name}</td>
                      <td style={{ fontWeight: 500 }}>{line.subcategory_name}</td>
                      <td style={{ textAlign: "right", fontWeight: 600 }}>
                        {formatCurrency(line.budget)}
                      </td>
                      {line.budget_method === "loan" && line.loan_total ? (
                        <LoanBar totalRepaid={line.total_repaid ?? 0} loanTotal={line.loan_total} loanMonthExpense={line.loan_month_expense} budget={line.budget} loanEndDate={line.loan_end_date} />
                      ) : line.budget_method === "provision" ? (
                        <ProvisionBar totalSpentProvision={line.total_spent_provision ?? 0} cumulativeBalance={line.cumulative_balance ?? 0} annualTarget={line.provision_annual_target ?? 0} />
                      ) : line.budget_method === "saving" && line.saving_target ? (
                        <SpendingBar spent={line.total_saved} budget={line.saving_target} isSaving targetDate={line.saving_target_date} />
                      ) : (
                        <SpendingBar spent={line.spent} budget={line.budget} />
                      )}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
